import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type Endpoint = {
  id: string;
  name: string;
  url: string;
  secret: string;
  events: string[];
  is_active: boolean;
  failure_count: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  description: string | null;
  created_at: string;
};

type Delivery = {
  id: string;
  endpoint_id: string | null;
  event: string;
  order_id: string | null;
  status: string;
  http_status: number | null;
  error_message: string | null;
  attempts: number;
  delivered_at: string | null;
  response_body: string | null;
  created_at: string;
};

const ALL_EVENTS = ["order.created", "order.paid", "order.cancelled"];

const EMPTY: Partial<Endpoint> = {
  name: "",
  url: "",
  secret: "",
  events: [...ALL_EVENTS],
  is_active: true,
  description: "",
};

function randomSecret(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function fmt(d: string | null): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export function WebhooksManager({ lang = "es" }: { lang?: "es" | "en" }) {
  const T = lang === "es"
    ? {
        title: "Webhooks salientes",
        subtitle: "Notifica a sistemas externos cuando ocurren eventos de órdenes.",
        newEndpoint: "Nuevo endpoint",
        edit: "Editar",
        save: "Guardar",
        cancel: "Cancelar",
        del: "Eliminar",
        delConfirm: "¿Eliminar este endpoint? Esta acción no se puede deshacer.",
        name: "Nombre",
        url: "URL",
        secret: "Secreto HMAC",
        gen: "Generar",
        copy: "Copiar",
        events: "Eventos",
        active: "Activo",
        desc: "Descripción",
        empty: "Aún no hay endpoints configurados.",
        deliveries: "Últimas entregas",
        deliveriesEmpty: "Sin entregas registradas.",
        failures: "Fallos consecutivos",
        lastSuccess: "Último éxito",
        lastFailure: "Último fallo",
        status: "Estado",
        when: "Fecha",
        event: "Evento",
        http: "HTTP",
        attempts: "Intentos",
        viewBody: "Ver respuesta",
      }
    : {
        title: "Outbound webhooks",
        subtitle: "Notify external systems when order events happen.",
        newEndpoint: "New endpoint",
        edit: "Edit",
        save: "Save",
        cancel: "Cancel",
        del: "Delete",
        delConfirm: "Delete this endpoint? This cannot be undone.",
        name: "Name",
        url: "URL",
        secret: "HMAC secret",
        gen: "Generate",
        copy: "Copy",
        events: "Events",
        active: "Active",
        desc: "Description",
        empty: "No endpoints configured yet.",
        deliveries: "Recent deliveries",
        deliveriesEmpty: "No deliveries recorded.",
        failures: "Consecutive failures",
        lastSuccess: "Last success",
        lastFailure: "Last failure",
        status: "Status",
        when: "When",
        event: "Event",
        http: "HTTP",
        attempts: "Attempts",
        viewBody: "View response",
      };

  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Endpoint> | null>(null);
  const [busy, setBusy] = useState(false);
  const [showBody, setShowBody] = useState<Delivery | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [eps, dels] = await Promise.all([
      supabase.from("webhook_endpoints").select("*").order("created_at", { ascending: false }),
      supabase.from("webhook_deliveries").select("*").order("created_at", { ascending: false }).limit(50),
    ]);
    if (eps.error) console.error(eps.error);
    if (dels.error) console.error(dels.error);
    setEndpoints((eps.data ?? []) as Endpoint[]);
    setDeliveries((dels.data ?? []) as Delivery[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...EMPTY, secret: randomSecret() });
  const startEdit = (ep: Endpoint) => setEditing({ ...ep });

  const toggleEvent = (ev: string) => {
    if (!editing) return;
    const list = editing.events ?? [];
    setEditing({
      ...editing,
      events: list.includes(ev) ? list.filter((e) => e !== ev) : [...list, ev],
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim() || !editing.url?.trim() || !editing.secret?.trim()) {
      alert(lang === "es" ? "Nombre, URL y secreto son obligatorios." : "Name, URL and secret are required.");
      return;
    }
    if (!/^https?:\/\//i.test(editing.url)) {
      alert(lang === "es" ? "La URL debe empezar con http(s)://" : "URL must start with http(s)://");
      return;
    }
    if (!editing.events || editing.events.length === 0) {
      alert(lang === "es" ? "Selecciona al menos un evento." : "Select at least one event.");
      return;
    }
    setBusy(true);
    const payload = {
      name: editing.name.trim(),
      url: editing.url.trim(),
      secret: editing.secret.trim(),
      events: editing.events,
      is_active: editing.is_active ?? true,
      description: editing.description?.trim() || null,
    };
    const res = editing.id
      ? await supabase.from("webhook_endpoints").update(payload).eq("id", editing.id)
      : await supabase.from("webhook_endpoints").insert(payload);
    setBusy(false);
    if (res.error) {
      alert(res.error.message);
      return;
    }
    setEditing(null);
    load();
  };

  const toggleActive = async (ep: Endpoint) => {
    const { error } = await supabase
      .from("webhook_endpoints")
      .update({ is_active: !ep.is_active })
      .eq("id", ep.id);
    if (error) alert(error.message);
    else load();
  };

  const remove = async (ep: Endpoint) => {
    if (!confirm(T.delConfirm)) return;
    const { error } = await supabase.from("webhook_endpoints").delete().eq("id", ep.id);
    if (error) alert(error.message);
    else load();
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const endpointName = (id: string | null) =>
    endpoints.find((e) => e.id === id)?.name ?? "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>{T.title}</h3>
          <div style={{ fontSize: 12, color: "var(--tx3)" }}>{T.subtitle}</div>
        </div>
        <button className="btn bp bs" onClick={startNew}>+ {T.newEndpoint}</button>
      </div>

      {loading ? (
        <div style={{ color: "var(--tx3)", fontSize: 12 }}>…</div>
      ) : endpoints.length === 0 ? (
        <div style={{ padding: 16, background: "var(--bg2)", border: "1px dashed var(--bd)", borderRadius: "var(--r1)", fontSize: 12, color: "var(--tx3)" }}>{T.empty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {endpoints.map((ep) => (
            <div key={ep.id} style={{ padding: 12, border: "1px solid var(--bd)", borderRadius: "var(--r1)", background: "var(--bg2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: ep.is_active ? "#16a34a" : "#94a3b8" }} />
                  <b style={{ fontSize: 13 }}>{ep.name}</b>
                  {ep.failure_count > 0 && (
                    <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}>
                      {T.failures}: {ep.failure_count}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn bo bs" onClick={() => toggleActive(ep)}>
                    {ep.is_active ? (lang === "es" ? "Desactivar" : "Disable") : (lang === "es" ? "Activar" : "Enable")}
                  </button>
                  <button className="btn bo bs" onClick={() => startEdit(ep)}>{T.edit}</button>
                  <button className="btn bo bs" onClick={() => remove(ep)} style={{ color: "#991B1B" }}>{T.del}</button>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 6, wordBreak: "break-all" }}>{ep.url}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {ALL_EVENTS.map((ev) => (
                  <span key={ev} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: ep.events.includes(ev) ? "var(--ac2)" : "var(--bg)", color: ep.events.includes(ev) ? "var(--ac3)" : "var(--tx3)", border: "1px solid var(--bd2)" }}>
                    {ev}
                  </span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, fontSize: 11, color: "var(--tx3)" }}>
                <div>{T.lastSuccess}: <span style={{ color: "var(--tx2)" }}>{fmt(ep.last_success_at)}</span></div>
                <div>{T.lastFailure}: <span style={{ color: "var(--tx2)" }}>{fmt(ep.last_failure_at)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: "var(--r1)", padding: 16, maxWidth: 560, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>{editing.id ? T.edit : T.newEndpoint}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
              <label style={{ fontSize: 11, color: "var(--tx3)" }}>{T.name}
                <input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} style={inputStyle} />
              </label>
              <label style={{ fontSize: 11, color: "var(--tx3)" }}>{T.url}
                <input value={editing.url ?? ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://billing.example.com/webhooks/nutrimart" style={inputStyle} />
              </label>
              <div>
                <div style={{ fontSize: 11, color: "var(--tx3)" }}>{T.secret}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={editing.secret ?? ""} onChange={(e) => setEditing({ ...editing, secret: e.target.value })} style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }} />
                  <button className="btn bo bs" type="button" onClick={() => setEditing({ ...editing, secret: randomSecret() })}>{T.gen}</button>
                  <button className="btn bo bs" type="button" onClick={() => copy(editing.secret ?? "")}>{T.copy}</button>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 4 }}>{T.events}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ALL_EVENTS.map((ev) => {
                    const on = (editing.events ?? []).includes(ev);
                    return (
                      <button key={ev} type="button" onClick={() => toggleEvent(ev)} className="btn bs" style={{ background: on ? "var(--ac)" : "var(--bg2)", color: on ? "#fff" : "var(--tx2)", border: "1px solid var(--bd)" }}>
                        {ev}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label style={{ fontSize: 11, color: "var(--tx3)", display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={editing.is_active ?? true} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                {T.active}
              </label>
              <label style={{ fontSize: 11, color: "var(--tx3)" }}>{T.desc}
                <textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 14 }}>
              <button className="btn bo" onClick={() => setEditing(null)} disabled={busy}>{T.cancel}</button>
              <button className="btn bp" onClick={save} disabled={busy}>{T.save}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <h4 style={{ margin: "12px 0 8px", fontSize: 14 }}>{T.deliveries}</h4>
        {deliveries.length === 0 ? (
          <div style={{ padding: 12, background: "var(--bg2)", border: "1px dashed var(--bd)", borderRadius: "var(--r1)", fontSize: 12, color: "var(--tx3)" }}>{T.deliveriesEmpty}</div>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid var(--bd)", borderRadius: "var(--r1)" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead style={{ background: "var(--bg2)" }}>
                <tr>
                  <th style={th}>{T.when}</th>
                  <th style={th}>{T.event}</th>
                  <th style={th}>Endpoint</th>
                  <th style={th}>{T.status}</th>
                  <th style={th}>{T.http}</th>
                  <th style={th}>{T.attempts}</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id} style={{ borderTop: "1px solid var(--bd2)" }}>
                    <td style={td}>{fmt(d.created_at)}</td>
                    <td style={td}><code>{d.event}</code></td>
                    <td style={td}>{endpointName(d.endpoint_id)}</td>
                    <td style={td}>
                      <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: d.status === "success" ? "#DCFCE7" : d.status === "failed" ? "#FEE2E2" : "#FEF3C7", color: d.status === "success" ? "#166534" : d.status === "failed" ? "#991B1B" : "#92400E" }}>{d.status}</span>
                    </td>
                    <td style={td}>{d.http_status ?? "—"}</td>
                    <td style={td}>{d.attempts}</td>
                    <td style={td}>
                      {(d.response_body || d.error_message) && (
                        <button className="btn bo bs" onClick={() => setShowBody(d)}>{T.viewBody}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBody && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={() => setShowBody(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: "var(--r1)", padding: 16, maxWidth: 720, width: "100%", maxHeight: "85vh", overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <b style={{ fontSize: 13 }}>{showBody.event} · {showBody.status}</b>
              <button className="btn bo bs" onClick={() => setShowBody(null)}>×</button>
            </div>
            {showBody.error_message && (
              <pre style={preStyle}>{showBody.error_message}</pre>
            )}
            {showBody.response_body && (
              <pre style={preStyle}>{showBody.response_body}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = { width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bd)", background: "var(--bg)", color: "var(--tx)", fontSize: 12, marginTop: 4 };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 11, color: "var(--tx3)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 10px", verticalAlign: "middle" };
const preStyle: React.CSSProperties = { background: "var(--bg2)", padding: 10, borderRadius: 6, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 400, overflow: "auto" };